import Vue from 'vue';
import OnboardingContainer from '@/modules/onboarding/OnboardingContainer.vue';

export interface StandaloneBubbleOptions {
  /** CSS selector of the element the bubble points at (must already be in the DOM). */
  target: string;
  /** Bubble body (HTML, rendered v-html-safe). */
  content: string;
  /** Label of the single dismiss button. */
  buttonLabel: string;
  /** vue-tour placement relative to the target. Defaults to `'right'`. */
  placement?: 'top' | 'bottom' | 'left' | 'right';
}

const RENDER_TIMEOUT_MS = 1000;
const TARGET_WATCH_MS = 500;
// Type-forced placeholder: TourStepButton.action is required, but in standalone mode OnboardingStep
// emits `dismiss` instead of calling it, so it is never invoked.
const NO_OP = () => undefined;

let counter = 0;

/**
 * Shows a single onboarding-style bubble (vue-tour) glued to `target`, reusing the onboarding
 * components in `standalone` mode — independent of the tutorial gating/START flow. Resolves when the
 * user dismisses it (✕ or button). REJECTS if the bubble fails to render (so callers can fall back),
 * mirroring `Tour.mount` (mount by selector) and accounting for vue-tour's start() rendering on a
 * later macrotask (`setTimeout(startTimeout)`), so the render check must poll, not run synchronously.
 */
export function showStandaloneBubble(options: StandaloneBubbleOptions): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const mountElement = document.getElementsByClassName('v-application--wrap')[0];
    if (!mountElement) {
      reject(new Error('standaloneBubble: no .v-application--wrap to mount into'));
      return;
    }

    const name = `standalone-bubble-${++counter}`;
    const host = document.createElement('div');
    host.id = name;
    mountElement.appendChild(host);

    let settled = false;
    let container: Vue | null = null;

    const teardown = () => {
      Vue.prototype.$tours?.[name]?.stop();
      const el = container?.$el;
      try {
        container?.$destroy();
      } catch { /* already destroyed */ }
      el?.parentNode?.removeChild(el);
      host.parentNode?.removeChild(host);
      // vue-tour's <v-tour> registers itself in $tours.mounted() but its beforeDestroy() never
      // removes the entry — delete it ourselves so unbounded bubble names don't leak detached tours.
      if (Vue.prototype.$tours) {
        delete Vue.prototype.$tours[name];
      }
    };

    const step = {
      target: options.target,
      content: options.content,
      params: {
        highlight: true,
        placement: options.placement ?? 'right',
        enableScrolling: false,
      },
      isPreviousButtonHidden: true,
      isNextButtonHidden: true,
      customButtons: [{ label: options.buttonLabel, primary: true, action: NO_OP, }],
    };

    const ContainerClass = Vue.extend(OnboardingContainer);
    const instance = new ContainerClass({ propsData: { tourName: name, steps: [step], standalone: true, }, });
    container = instance;
    (instance as any).$vuetify = Vue.prototype.$vuetify;
    (instance as any).$tours = Vue.prototype.$tours;

    instance.$on('dismiss', () => {
      if (settled) {
        return;
      }
      settled = true;
      teardown();
      resolve();
    });

    try {
      // Mount BY SELECTOR (like Tour.mount) so Vue replaces the #name host with the component root.
      instance.$mount(`#${name}`);
    } catch (error) {
      if (!settled) {
        settled = true;
        teardown();
        reject(error instanceof Error ? error : new Error(String(error)));
      }
      return;
    }

    // The container self-starts (standalone); vue-tour renders the step on a later macrotask, then
    // popper positions it. Poll the mounted subtree for the rendered bubble; reject if it never shows.
    const rootEl = instance.$el as HTMLElement;
    const POLL_MS = 50;
    let waited = 0;
    // Bubble żyje do dismissu, więc przetrwa nawigację — a gdy target zniknie z DOM (np. wejście
    // na widok bez menu bocznego), popper odkłada go w lewy górny róg NAD treścią strony.
    // Wtedy sprzątamy i odrzucamy, żeby caller pokazał zwykły modal.
    const watchTarget = () => {
      if (settled) {
        return;
      }
      if (!document.querySelector(options.target)) {
        settled = true;
        teardown();
        reject(new Error('standaloneBubble: target left the DOM'));
        return;
      }
      setTimeout(watchTarget, TARGET_WATCH_MS);
    };
    const poll = () => {
      if (settled) {
        return;
      }
      // Wyrenderowany → dalej tylko pilnujemy, czy target nie zniknął.
      if (rootEl?.querySelector('.v-step')) {
        setTimeout(watchTarget, TARGET_WATCH_MS);
        return;
      }
      if ((waited += POLL_MS) >= RENDER_TIMEOUT_MS) {
        settled = true;
        teardown();
        reject(new Error('standaloneBubble: bubble did not render (no .v-step)'));
        return;
      }
      setTimeout(poll, POLL_MS);
    };
    setTimeout(poll, POLL_MS);
  });
}
